import { Component, OnDestroy, ViewChild } from '@angular/core';
import { ModalComponent } from "../modal/modal.component";
import { ModalSize } from "../modal/modal-size.enum";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

@Component({
  selector: 'app-document-viewer-modal',
  templateUrl: './document-viewer-modal.component.html',
  styleUrl: './document-viewer-modal.component.scss'
})
export class DocumentViewerModalComponent implements OnDestroy {
  @ViewChild('modal') modal: ModalComponent;

  documentUrl: SafeResourceUrl = null;
  documentType: 'pdf' | 'image' | 'unsupported' = 'unsupported';
  fileName: string = '';
  isLoading: boolean = false;

  private blobUrl: string = null;

  // File extensions that can be previewed
  private readonly imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
  private readonly pdfExtensions = ['.pdf'];

  constructor(private sanitizer: DomSanitizer) {}

  show(blob: Blob, fileName: string) {
    this.isLoading = true;
    this.fileName = fileName;
    this.documentType = this.getDocumentType(fileName);

    if (this.documentType === 'unsupported') {
      this.isLoading = false;
      this.modal.show();
      return;
    }

    // Create blob URL
    this.blobUrl = URL.createObjectURL(blob);
    this.documentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
    this.isLoading = false;
    this.modal.show();
  }

  hide() {
    this.cleanup();
    this.modal.hide();
  }

  onDocumentLoad() {
    this.isLoading = false;
  }

  canPreview(fileName: string): boolean {
    const type = this.getDocumentType(fileName);
    return type === 'pdf' || type === 'image';
  }

  private getDocumentType(fileName: string): 'pdf' | 'image' | 'unsupported' {
    const extension = this.getFileExtension(fileName);

    if (this.pdfExtensions.includes(extension)) {
      return 'pdf';
    }

    if (this.imageExtensions.includes(extension)) {
      return 'image';
    }

    return 'unsupported';
  }

  private getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot === -1) return '';
    return fileName.substring(lastDot).toLowerCase();
  }

  private cleanup() {
    // Revoke the blob URL to free memory
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    this.documentUrl = null;
    this.fileName = '';
    this.documentType = 'unsupported';
  }

  ngOnDestroy() {
    this.cleanup();
  }

  protected readonly ModalSize = ModalSize;
}
